import React from "react";

import { Redirect, RouteComponentProps } from "react-router-dom";

import { t } from "@lingui/macro";
import { withSnackbar, WithSnackbarProps } from "notistack";
import { UseFormReturn } from "react-hook-form";

import { Series } from "models";
import { ErrorResponseApi, GetCollectionListResponse } from "models/response";

import { Box } from "@mui/material";

import { ZPlayLoader, ZPlayNetworkService, ZPlayUploadLoader } from "commons";
import NavigationBack from "commons/navigation/navigation-back/navigation-back";
import CreateAndEditSeriesDetailForm from "components/backoffice/series/create-and-edit-series-detail-form/create-and-edit-series-detail-form";
import { ContentProducerService } from "services/content-producer-service";

export interface EditSeriesState {
    series: Series | null,
    collectionsList: GetCollectionListResponse | null
    isLoading: boolean,
    getSeriesError: boolean,
    thumbnailFile: File | null,
    isThumbnailUpdated: boolean,
    isVodUpdated: boolean,
    isUploading: boolean,
    uploadStageMessage: string,
    currentUploadPercentage: number
}

class EditSeries extends React.Component<UseFormReturn & WithSnackbarProps & RouteComponentProps<any>, EditSeriesState> {


    constructor(props: any) {

        super(props);

        this.state = {
            isLoading: true,
            series: null,
            collectionsList: null,
            getSeriesError: false,
            thumbnailFile: null,
            isThumbnailUpdated: false,
            isVodUpdated: false,
            isUploading: false,
            uploadStageMessage: "",
            currentUploadPercentage: 0
        };
    }

    public componentDidMount() {
        this.getSeriesById();
    };

    getSeriesSuccessCallback = (data: Series) => {

        this.setState(() => ({
            series: data
        }))

        ContentProducerService.getCollectionList(this.getCollectionListSuccessCallback, this.getCollectionListErrorCallback);
    }

    getSeriesErrorCallback = (err: ErrorResponseApi) => {
        this.setState(() => ({
            isLoading: false,
            series: null,
            getSeriesError: true
        }));
    }

    getCollectionListSuccessCallback = (data: GetCollectionListResponse) => {
        this.setState(() => ({
            collectionsList: data,
            isLoading: false
        }))

    }

    getCollectionListErrorCallback = (err: ErrorResponseApi) => {

        let emptyCollectionList: GetCollectionListResponse = {
            awardList: { list: [], size: 0 },
            countryList: { list: [], size: 0 },
            festivalList: { list: [], size: 0 },
            genreList: { list: [], size: 0 },
            languageList: { list: [], size: 0 },
            tagList: { list: [], size: 0 },
            contentIssuesList: { list: [], size: 0 },
            seriesList: { list: [], size: 0 }
        };

        this.setState(() => ({
            isLoading: false,
            collectionsList: emptyCollectionList
        }));

        this.props.enqueueSnackbar(t({
            id: "ec-error-get-collection-list-error",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati"
        }), {
            variant: "error",
        });
    }

    private onSubmit = (request: any) => {

        if (!this.checkDetailsStepValidity(request))
            return

        let thumbnailFile = request.thumbnailFile;

        if (request.isThumbnailUpdated) {
            request.thumbnailData = {
                fileName: thumbnailFile.name,
                format: thumbnailFile.type.split("/")[1]
            };
        } else {
            request.thumbnailData = this.state.series?.thumbnailData
        }

        this.setState(() => ({
            thumbnailFile: thumbnailFile,
            isThumbnailUpdated: request.isThumbnailUpdated,
            isUploading: true,
            uploadStageMessage: t({
                id: "ec-update-pre-stage",
                message: "Preparazione alla modifica"
            })
        }))

        ContentProducerService.updateSeries(this.state.series?.id!, request, this.startUpload, this.onSubmitCallbackError)
    }

    onSubmitCallback = () => {

        this.props.enqueueSnackbar(t({
            id: "ec-error-submit-update-series",
            message: "Il contenuto è stato aggiornato"
        }), {
            variant: "success",
        });

        this.getSeriesById();
    }

    onSubmitCallbackError = (err: ErrorResponseApi) => {

        this.setState(() => ({
            isLoading: false,
            isUploading: false
        }));

        this.props.enqueueSnackbar(t({
            id: "ec-error-submit-update-series-error",
            message: "Si è verificato un errore. Non è stato possibile modificare il contenuto"
        }), {
            variant: "error",
        });
    }

    private startUpload = (data: any) => {

        if (this.state.isThumbnailUpdated) {

            this.setState(() => ({
                uploadStageMessage: t({
                    id: "ec-upload-thumbnail-stage",
                    message: "Caricamento della thumbnail in corso..."
                })
            }));

            ZPlayNetworkService.xhrMultipartUpload(data.s3PresignedThumbnailUrl ?? "", this.state.thumbnailFile, this.progressCallback)
                .then(this.confirmThumbUpload)
                .catch(this.errorCallback)
        }

        if (!this.state.isThumbnailUpdated && !this.state.isVodUpdated) {
            this.onSubmitCallback();
        }
    };

    private errorCallback = (err: any) => {
        this.props.enqueueSnackbar(t({
            id: "ec-error-submit-update-series-error",
            message: "Si è verificato un errore. Non è stato possibile modificare il contenuto"
        }), {
            variant: "error",
        });
        this.setState(() => ({ isUploading: false }));
    };

    private confirmThumbUpload = () => {

        this.setState(() => ({
            currentUploadPercentage: 0
        }));

        this.thumbUploadUploadEnd()
    };

    private getSeriesById = () => {

        this.setState(() => ({
            isUploading: false,
            isLoading: true
        }));

        ContentProducerService.getSeriesById(this.props.match.params.id, this.getSeriesSuccessCallback, this.getSeriesErrorCallback);
    }

    private thumbUploadUploadEnd = () => {

        if (!this.state.isVodUpdated) {
            this.getSeriesById();
        }

    };

    private progressCallback = (percentage: number) => {
        this.setState(() => ({ currentUploadPercentage: percentage }))
    }

    private checkDetailsStepValidity = (submitData: any): boolean => {

        let errorsArray: string[] = [];

        if (!submitData.segregationType || submitData.segregationType === "")
            errorsArray.push(t({
                id: "ec-form-validation-no-segregation",
                message: "È necessario inserire la segregazione"
            }));

        if (submitData.title.length === 0)
            errorsArray.push(t({
                id: "ec-form-validation-no-title",
                message: "È necessario inserire almeno un titolo"
            }));

        if (submitData.synopsis.length === 0)
            errorsArray.push(t({
                id: "ec-form-validation-no-synopsis",
                message: "È necessario inserire almeno una sinossi"
            }));

        if (submitData.isThumbnailUpdated && !submitData.thumbnailFile.name)
            errorsArray.push(t({
                id: "ec-form-validation-no-thumbnail-file",
                message: "È necessario inserire un'immagine di copertina"
            }));

        if (!submitData.isThumbnailUpdated && !submitData.thumbnailFile.fileName)
            errorsArray.push(t({
                id: "ec-form-validation-no-thumbnail-file",
                message: "È necessario inserire un'immagine di copertina"
            }));

        return errorsArray.length === 0;
    }


    render() {
        return <Box sx={{ p: 5 }}>
            {this.state.isLoading && <ZPlayLoader />}
            {this.state.getSeriesError && <Redirect to={"/not-found"} />}
            {this.state.isUploading &&
                <ZPlayUploadLoader progress={this.state.currentUploadPercentage}
                    message={this.state.uploadStageMessage} />}
            {!this.state.isLoading && <>
                <NavigationBack />
                {this.state.series !== null &&
                    <Box maxWidth={"1000px"} padding={"30px 10px 10px"} margin={"0 auto"}>
                        <CreateAndEditSeriesDetailForm
                            onDetailSubmit={this.onSubmit}
                            series={this.state.series}
                            collectionsList={this.state.collectionsList}
                        />
                    </Box>}
            </>}
        </Box>;
    }
}

export default withSnackbar(EditSeries);